import React, { useEffect, useId, useRef, useState } from "react";
import { RosterPatient, StatusReason, StatusSaveResult, patientName, providerApi } from "../providerApi";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../../components/shadcn/ui/dialog";
import "./status-field.css";

/**
 * The resident's status, as a drop-down beside the room (provider Eric Parker via Isaiah Cotto, 2026-10-22; backend
 * migration 20261022d, docs/provider/RESIDENT_STATUS.md): "residents have been discharged while the row still says
 * Active".
 *
 * Rulings (Isaiah Cotto, 2026-10-22):
 *   - Inactive needs a reason (Discharged, Moved, Other) and may carry a short note; Active may be chosen to revert.
 *   - "Report deceased" asks Finance to confirm (a REQUEST, never a status, no effect until Finance confirms): the row
 *     says "Death reported, waiting on Finance", with Withdraw for the provider who filed it (or the office). Earlier:
 *     Finance" and the status itself does not change here.
 *   - Prospect is never offered; when it is the current value it shows, read-only. No status recorded offers Active
 *     and Inactive.
 *
 * Monday's status is never changed: what is saved is eMerge's own status for the resident. A status set in eMerge
 * carries an "edited" mark naming Monday's word. The box is offered only where the server said it would accept the save
 * (`status_editable` on the row); everywhere else the status is plain text. A native <select>, so the platform's own
 * picker serves keyboards, screen readers and phones. If the server refuses, the old status comes back and the reason
 * is said.
 */

type Shown = Pick<RosterPatient, "status" | "status_edited" | "status_monday" | "status_reason" | "status_note" | "death_reported" | "death_request">;

const REASON_LABEL: Record<StatusReason, string> = { DISCHARGED: "Discharged", MOVED: "Moved", OTHER: "Other" };
const DEFAULT_REASONS: StatusReason[] = ["DISCHARGED", "MOVED", "OTHER"];
const NOTE_MAX = 200;
const NO_STATUS = "No status recorded";
const CLEAR = "__clear";
const DECEASED = "__deceased";
const CURRENT = "__current";
const REASON_EDIT = "__reason";

const label = (s: string | null | undefined) => (s && s.trim() ? s : NO_STATUS);
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** The small "edited" mark, with Monday's status in its tooltip and its accessible text. */
export function StatusEditedMark({ monday, reason, id }: { monday: string | null | undefined; reason?: StatusReason | null; id?: string }) {
  const why = reason ? ` Reason: ${REASON_LABEL[reason] || reason}.` : "";
  const words = `Set in eMerge.${why} The manifest status from Monday is ${monday ? monday : "not recorded"}.`;
  return (
    <span className="fw-room-edited fw-status-edited" title={words} id={id}>
      edited<span className="pv-sr-only"> — {words}</span>
    </span>
  );
}

/** "Death reported, waiting on Finance": an open request (ruling 2026-10-22). */
export function DeathReportedMark() {
  return (
    <span className="fw-status-death" title="A death was reported and Finance has not confirmed it yet. Nothing changes until Finance confirms it.">
      Death reported, waiting on Finance
    </span>
  );
}

/** Finance recorded the death (the report). The status shown is still the manifest's until it changes there. */
export function DeathRecordedMark() {
  return (
    <span className="fw-status-death" title="Finance has recorded this resident's death.">
      Death recorded by Finance
    </span>
  );
}

type Phase = { kind: "idle" } | { kind: "saving" } | { kind: "saved"; words: string } | { kind: "error"; message: string };
type Panel = { kind: "none" } | { kind: "inactive" } | { kind: "deceased" };

const fromPatient = (p: Shown): Shown => ({
  status: p.status ?? null,
  status_edited: Boolean(p.status_edited),
  status_monday: p.status_monday === undefined ? p.status ?? null : p.status_monday,
  status_reason: p.status_reason ?? null,
  // Undefined: the note was not released to this caller (it is with the care-assigned fields). Never treated as none.
  status_note: p.status_note === undefined ? undefined : p.status_note ?? null,
  death_reported: Boolean(p.death_reported),
  death_request: p.death_request ?? null,
});

export function StatusField({
  facilityId,
  patient,
  editable,
  offline,
  reasons = DEFAULT_REASONS,
  noteMax = NOTE_MAX,
  onSaved,
}: {
  facilityId: string;
  patient: Pick<RosterPatient, "id" | "first_name" | "last_name"> & Shown;
  editable: boolean;
  offline: boolean;
  reasons?: StatusReason[];
  noteMax?: number;
  onSaved?: (patientId: string, status: Shown) => void;
}) {
  const [shown, setShown] = useState<Shown>(() => fromPatient(patient));
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [panel, setPanel] = useState<Panel>({ kind: "none" });
  const [reason, setReason] = useState<StatusReason | "">("");
  const [note, setNote] = useState("");
  const [noteTouched, setNoteTouched] = useState(false);
  const [deathDate, setDeathDate] = useState(todayIso());
  const busy = useRef(false);
  const markId = useId();
  const panelId = useId();
  const selectRef = useRef<HTMLSelectElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const name = patientName(patient);

  // The Inactive panel (an inline disclosure) takes the focus when it opens, on the chosen or first reason; closing it
  // gives the focus back to the drop-down. The death report is a modal Dialog: it focuses its date itself.
  useEffect(() => {
    if (panel.kind === "inactive") {
      const root = panelRef.current;
      const target = root?.querySelector<HTMLInputElement>('input[type="radio"]:checked') || root?.querySelector<HTMLInputElement>('input[type="radio"]');
      target?.focus();
    }
  }, [panel.kind]);

  // A newer day sheet (a refresh) replaces what is shown, unless a save is in flight or a panel is open.
  useEffect(() => {
    if (busy.current || panel.kind !== "none") return;
    setShown(fromPatient(patient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.status, patient.status_edited, patient.status_monday, patient.status_reason, patient.status_note, patient.death_reported, patient.death_request?.id, patient.death_request?.withdrawable]);

  const marks = (
    <>
      {shown.status_edited && (
        <>
          {" "}
          <StatusEditedMark monday={shown.status_monday} reason={shown.status_reason} id={markId} />
        </>
      )}
      {shown.death_reported ? (
        <>
          {" "}
          <DeathRecordedMark />
        </>
      ) : shown.death_request ? (
        <>
          {" "}
          <DeathReportedMark />
          {editable && shown.death_request.withdrawable && (
            <>
              {" "}
              <button
                type="button"
                className="fw-status-withdraw"
                disabled={offline || phase.kind === "saving"}
                onClick={() => void withdrawRequest()}
                aria-label={`Withdraw the death report for ${name}`}
              >
                Withdraw
              </button>
            </>
          )}
        </>
      ) : null}
    </>
  );

  if (!editable) {
    return (
      <span className="fw-status">
        {label(shown.status)}
        {marks}
      </span>
    );
  }

  const current = shown.status ?? null;
  const settable = current === "Active" || current === "Inactive";
  const closePanel = () => {
    setPanel({ kind: "none" });
    setReason("");
    setNote("");
    window.setTimeout(() => selectRef.current?.focus(), 0);
  };

  const save = async (input: { status: "Active" | "Inactive" | null; reason?: StatusReason; note?: string }) => {
    if (busy.current) return;
    if (offline) {
      setPhase({ kind: "error", message: "You are offline. The status was not saved." });
      return;
    }
    const before = shown;
    // Optimistic: the new status shows at once; a clear shows Monday's again.
    const monday = shown.status_monday ?? null;
    const optimistic: Shown = input.status === null
      ? { ...before, status: monday, status_edited: false, status_reason: null, status_note: null }
      : {
        ...before, status: input.status, status_edited: input.status !== monday || input.status === "Inactive", status_reason: input.reason || null,
        status_note: input.status !== "Inactive" ? null : input.note !== undefined ? input.note || null : before.status_note,
      };
    setShown(optimistic);
    setPhase({ kind: "saving" });
    busy.current = true;
    try {
      const r = await providerApi.setStatus(facilityId, patient.id, { ...input, expected_status: before.status ?? null });
      if (r.success && r.data) {
        const d: StatusSaveResult = r.data;
        const saved: Shown = {
          ...before, status: d.status, status_edited: d.status_edited, status_monday: d.status_monday,
          status_reason: d.status_reason, status_note: d.status_note,
        };
        setShown(saved);
        setPhase({ kind: "saved", words: d.status === "Inactive" ? "Saved. This resident leaves the manifest and census when the list refreshes." : "Saved" });
        if (onSaved) onSaved(patient.id, saved);
      } else {
        setShown(before);
        setPhase({ kind: "error", message: r.message || r.error || "The status was not saved." });
      }
    } catch {
      setShown(before);
      setPhase({ kind: "error", message: "Could not reach the server. The status was not saved." });
    } finally {
      busy.current = false;
    }
  };

  const report = async () => {
    if (busy.current) return;
    if (offline) {
      setPhase({ kind: "error", message: "You are offline. Nothing was reported." });
      return;
    }
    setPhase({ kind: "saving" });
    busy.current = true;
    try {
      const r = await providerApi.reportDeath(facilityId, patient.id, deathDate);
      if (r.success && r.data) {
        const saved: Shown = { ...shown, death_request: { id: r.data.request_id, effective_date: deathDate, mine: true, withdrawable: true } };
        setShown(saved);
        setPanel({ kind: "none" });
        window.setTimeout(() => selectRef.current?.focus(), 0);
        setPhase({ kind: "saved", words: "Sent to Finance to confirm" });
        if (onSaved) onSaved(patient.id, saved);
      } else {
        setPhase({ kind: "error", message: r.message || r.error || "Nothing was reported." });
      }
    } catch {
      setPhase({ kind: "error", message: "Could not reach the server. Nothing was reported." });
    } finally {
      busy.current = false;
    }
  };

  const withdrawRequest = async () => {
    const req = shown.death_request;
    if (busy.current || !req) return;
    if (offline) {
      setPhase({ kind: "error", message: "You are offline. Nothing was withdrawn." });
      return;
    }
    setPhase({ kind: "saving" });
    busy.current = true;
    try {
      const r = await providerApi.withdrawDeathReport(facilityId, patient.id, req.id);
      if (r.success) {
        const saved: Shown = { ...shown, death_request: null };
        setShown(saved);
        setPhase({ kind: "saved", words: "Death report withdrawn" });
        window.setTimeout(() => selectRef.current?.focus(), 0);
        if (onSaved) onSaved(patient.id, saved);
      } else {
        setPhase({ kind: "error", message: r.message || r.error || "Nothing was withdrawn." });
      }
    } catch {
      setPhase({ kind: "error", message: "Could not reach the server. Nothing was withdrawn." });
    } finally {
      busy.current = false;
    }
  };

  const onChoose = (value: string) => {
    if (phase.kind !== "saving") setPhase({ kind: "idle" });
    if (value === "Inactive" || value === REASON_EDIT) {
      // Already Inactive: the panel opens on the reason and note on record, to change them.
      const editing = shown.status === "Inactive";
      setReason(editing && shown.status_reason ? shown.status_reason : "");
      setNote(editing && shown.status_note ? shown.status_note : "");
      setNoteTouched(false);
      setPanel({ kind: "inactive" });
    } else if (value === "Active") {
      setPanel({ kind: "none" });
      if (current !== "Active") void save({ status: "Active" });
    } else if (value === CLEAR) {
      setPanel({ kind: "none" });
      void save({ status: null });
    } else if (value === DECEASED) {
      setDeathDate(todayIso());
      setPanel({ kind: "deceased" });
    }
  };

  const inputId = `status-${patient.id}`;
  const statusWords = phase.kind === "saving" ? "Saving…" : phase.kind === "saved" ? phase.words : "";
  const onPanelKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closePanel();
    }
  };
  const disabled = offline || phase.kind === "saving";
  const noteHidden = shown.status === "Inactive" && shown.status_edited === true && shown.status_note === undefined;
  return (
    <span className="fw-status fw-status-editable">
      <label htmlFor={inputId} className="pv-sr-only">Status for {name}</label>
      <select
        id={inputId}
        ref={selectRef}
        className="fw-status-select"
        value={settable ? (current as string) : CURRENT}
        aria-describedby={shown.status_edited ? markId : undefined}
        aria-invalid={phase.kind === "error" ? true : undefined}
        disabled={disabled}
        title={offline ? "You are offline; the status cannot be saved now." : "Change the resident's status in eMerge. Monday's status is not changed."}
        onChange={(e) => onChoose(e.target.value)}
      >
        {!settable && (
          <option value={CURRENT} disabled>
            {label(current)}
          </option>
        )}
        <option value="Active">Active</option>
        <option value="Inactive">Inactive…</option>
        {current === "Inactive" && <option value={REASON_EDIT}>Change the reason…</option>}
        {shown.status_edited && <option value={CLEAR}>Back to the manifest's status ({label(shown.status_monday)})</option>}
        {!shown.death_reported && !shown.death_request && <option value={DECEASED}>Report deceased…</option>}
      </select>
      {marks}
      <span role="status" aria-live="polite" className="fw-room-status fw-status-words">
        {statusWords}
      </span>
      {/* While the death-report dialog is open its refusal is shown inside it (the row is behind the overlay). */}
      {phase.kind === "error" && panel.kind !== "deceased" && (
        <span role="alert" className="fw-error fw-room-error">
          {phase.message}
        </span>
      )}
      {panel.kind === "inactive" && (
        <span className="fw-status-panel" id={panelId} ref={panelRef} role="group" aria-label={`Mark ${name} Inactive`} onKeyDown={onPanelKey}>
          <span className="fw-status-panel-words">
            This resident will leave the manifest and census. Their chart stays open to you, and you can set Active again.
          </span>
          <fieldset className="fw-status-reasons">
            <legend>Why? (required)</legend>
            {reasons.map((r) => (
              <label key={r} className="fw-status-reason">
                <input
                  type="radio"
                  name={`status-reason-${patient.id}`}
                  value={r}
                  checked={reason === r}
                  onChange={() => setReason(r)}
                />{" "}
                {REASON_LABEL[r] || r}
              </label>
            ))}
          </fieldset>
          <label className="fw-status-note">
            Note (optional)
            <textarea
              rows={2}
              maxLength={noteMax}
              value={note}
              onChange={(e) => { setNote(e.target.value); setNoteTouched(true); }}
              placeholder={noteHidden ? "A note is on record; it is kept unless you write a new one" : "Where they went, if known"}
            />
          </label>
          <span className="fw-status-actions">
            <button
              type="button"
              className="fw-status-save"
              disabled={!reason || disabled}
              onClick={() => {
                if (!reason) return;
                setPanel({ kind: "none" });
                window.setTimeout(() => selectRef.current?.focus(), 0);
                // The note is sent when it is known here or was typed (an emptied box clears it); a note this caller
                // was not shown is left out, and the server keeps it.
                const sendNote = noteTouched || shown.status_note !== undefined;
                void save({ status: "Inactive", reason, ...(sendNote ? { note: note.trim() } : {}) });
              }}
            >
              {current === "Inactive" ? "Save reason" : "Mark Inactive"}
            </button>
            <button type="button" className="fw-status-cancel" onClick={closePanel}>
              Cancel
            </button>
          </span>
        </span>
      )}
      {panel.kind === "deceased" && (
        // A true modal (shadcn Dialog), unlike the inline Inactive panel: it files a request with Finance. Focus is trapped
        // and starts on the date, Escape and Cancel close it, focus goes back to the drop-down, the page behind is inert.
        <Dialog open onOpenChange={(o) => { if (!o) closePanel(); }}>
          <DialogContent
            hideClose
            className="fw-drawer w-auto max-w-[28rem] border-0 bg-transparent p-0 shadow-none sm:rounded-none"
            onOpenAutoFocus={(e) => { e.preventDefault(); dateRef.current?.focus(); }}
            onCloseAutoFocus={(e) => { e.preventDefault(); selectRef.current?.focus(); }}
          >
            <div className="fw-status-panel fw-status-dialog" style={{ margin: 0 }}>
              <DialogTitle className="sr-only">{`Report ${name} deceased`}</DialogTitle>
              <DialogDescription asChild>
                <span className="fw-status-panel-words">
                  This asks Finance to confirm. The status does not change here. Until Finance confirms it, the row says "Death
                  reported, waiting on Finance", and you can withdraw it.
                </span>
              </DialogDescription>
              {phase.kind === "error" && (
                <span role="alert" className="fw-error fw-room-error">
                  {phase.message}
                </span>
              )}
              <label className="fw-status-note">
                Date of death
                <input ref={dateRef} type="date" value={deathDate} max={todayIso()} onChange={(e) => setDeathDate(e.target.value)} required />
              </label>
              <span className="fw-status-actions">
                <button type="button" className="fw-status-save" disabled={!deathDate || disabled} onClick={() => void report()}>
                  Report to Finance
                </button>
                <button type="button" className="fw-status-cancel" onClick={closePanel}>
                  Cancel
                </button>
              </span>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </span>
  );
}
